import isNumber from 'lodash/isNumber';
import Utils from '../utils';
import Data from '../data';

const { Session } = Utils;
const { Carrier, Capability, InternalCapability } = Data;

class HeaderDataBuilder {
	static getConfig({ screenType, showChatWithUs }) {
		const { isSignedIn, settings } = Session.getData();
		this.screenType = screenType;
		this.showChatWithUs = showChatWithUs;

		const hasZeroPostageBalance =
			isSignedIn && Session.hasCapability(InternalCapability.ZeroPostageBalance);
		const isOrdersRestricted =
			isSignedIn && Session.hasCapability(Capability.Shipping) === false;
		const isOrdersHidden =
			hasZeroPostageBalance ||
			(isOrdersRestricted &&
				Session.hasCapability(InternalCapability.HideUnavailableFeatures));
		const { ConfiguredCarriers = [] } = this.accountInfo || {};

		ConfiguredCarriers.forEach(carrierObj => {
			if (carrierObj.Carrier.toLowerCase() === 'zorbit') {
				this.showZorbitMenuItems = true;
			}
		});

		// standard list of items applicable to all the device types
		let leftMenuItems = [
			{
				id: 'mail',
				label: 'Mail',
				visible: !hasZeroPostageBalance,
				isRootMenu: true,
			},
			{
				id: 'orders',
				label: 'Orders',
				visible: !isOrdersHidden,
				isRootMenu: true,
				isPlanUpgradeNeeded: !Session.hasCapability(Capability.CanUseOrders),
			},
			{
				id: 'marketing',
				label: 'Marketing',
				visible: !hasZeroPostageBalance && settings.ShowMarketing,
				isRootMenu: true,
			},
			{
				id: 'products',
				label: 'Products',
				visible:
					settings.HasShipStationToken === true &&
					!hasZeroPostageBalance &&
					Session.hasCapability(Capability.CanUseProducts),
				isRootMenu: true,
				isPlanUpgradeNeeded: !Session.hasCapability(Capability.CanUseProducts),
			},
			{
				id: 'contacts',
				label: 'Contacts',
				visible: !hasZeroPostageBalance,
				isRootMenu: true,
			},
			this.getHistoryMenuItem(),
			this.getReportsMenuItem(),
		];

		if (this.showZorbitMenuItems) {
			leftMenuItems.push({
				id: 'inbounds',
				label: 'Inbounds',
				visible: true,
				isRootMenu: true,
			});
		}

		// the default list is empty since it varies across the devices
		let rightMenuItems = [];

		if (this.screenType === 'desktop') {
			// desktop: supplies and help are on left side unlike tablet
			leftMenuItems.push(this.getSuppliesMenuItem(), this.getHelpMenuItem());

			if (isSignedIn) {
				// desktop: items shown in authenticated state
				rightMenuItems = [this.getBalanceMenuItem(), this.getMyAccountMenuItem()];
			} else {
				// desktop: items shown in unauthenticated state
				rightMenuItems = [
					{
						id: 'separator',
						separator: true,
						isRootMenu: true,
					},
					{
						id: 'signIn',
						label: 'Sign In',
						isRootMenu: true,
						items: [
							{
								id: 'signInform',
								label: '', // intentionally left blank
							},
							this.getLegalTerms(),
						],
					},
				];
			}
		} else if (this.screenType === 'tablet') {
			if (isSignedIn) {
				// tablet: items shown in authenticated state
				// note: left Item customization is not required here as standard leftItems serve the left menu.
				rightMenuItems = [
					{
						id: 'menutoggle',
						label: '', // intentionally left blank
						isRootMenu: true,
						items: [
							this.getMyAccountMenuItem(),
							this.getBalanceMenuItem(),
							this.getSuppliesMenuItem(),
							this.getHelpMenuItem(),
						],
					},
				];
			} else {
				rightMenuItems = [
					// tablet: items shown in unauthenticated state
					{
						id: 'menutoggle',
						isRootMenu: true,
						items: [
							this.getSuppliesMenuItem(),
							this.getSignInMenuItem(),
							this.getHelpMenuItem(),
						],
					},
				];
			}
		} else if (this.screenType === 'phone') {
			// adds supplies that is required for both auth and unauth states
			leftMenuItems.push(this.getSuppliesMenuItem());

			if (isSignedIn) {
				// phone: authenticated state
				leftMenuItems.push(
					this.getMyAccountMenuItem(),
					this.getBalanceMenuItem(),
					this.getHelpMenuItem()
				);
			} else {
				// phone: unauthenticated state
				leftMenuItems.push(this.getSignInMenuItem(), this.getHelpMenuItem());
			}
			leftMenuItems = [
				{
					id: 'menutoggle',
					isRootMenu: true,
					label: '', // intentionally left blank
					items: leftMenuItems,
				},
			];
			// in case of phone screentype, leftItems serve all the required header tabs
			rightMenuItems = [];
		}

		leftMenuItems = leftMenuItems.filter(item => item.visible !== false);

		rightMenuItems = rightMenuItems.filter(item => item.visible !== false);

		// finally returns header items generated based on the screen type
		return {
			left: leftMenuItems,
			right: rightMenuItems,
		};
	}

	static getHistoryMenuItem() {
		const { dynamicSettings, settings, isSignedIn, accountInfo } = Session.getData();

		let isShowContainerLabelBadge = false,
			isHideUnavailableFeatures = false,
			restrictHistory = false,
			restrictPrintHistory = false,
			restrictScanForm = false,
			isGaPickupCarrier = false,
			isShowRefundAssistLabelBadge = false;
		const { containerLabelCount, refundAssistLabelCount } = dynamicSettings;

		if (isSignedIn) {
			isHideUnavailableFeatures = Session.hasCapability(
				InternalCapability.HideUnavailableFeatures
			);
			restrictScanForm = !Session.hasCapability(Capability.Scanform);

			isGaPickupCarrier = accountInfo.globalpost?.firstMileCarrier === Carrier.USPS;

			// process restrictions for history
			if (
				Session.hasCapability(Capability.Shipping) ||
				Session.hasCapability(Capability.NetStamps) ||
				Session.hasCapability(Capability.International)
			) {
				restrictHistory = false;
			} else {
				restrictHistory = true;
			}

			// checks if container label & refund assist badge can be shown or not
			if (
				isGaPickupCarrier === true &&
				settings.Page.search(
					/webpostage|mail|orders|contacts|products|addressbook|reports|inbounds/i
				) !== -1
			) {
				if (containerLabelCount !== undefined && containerLabelCount > 0) {
					isShowContainerLabelBadge = true;
				}
			}

			if (refundAssistLabelCount !== undefined && refundAssistLabelCount > 0) {
				isShowRefundAssistLabelBadge = true;
			}

			// process restrictions for 'Search Print History'
			restrictPrintHistory =
				Session.hasCapability(InternalCapability.ViewOnlineReports) !== true ||
				Session.hasCapability(InternalCapability.ViewAdvancedReporting) !== true;
		}

		return {
			id: 'history',
			label: 'History',
			isRestricted: restrictHistory,
			badgeIndicator: isShowContainerLabelBadge || isShowRefundAssistLabelBadge,
			dataqa: 'history',
			isRootMenu: true,
			isRootClickable: true,
			items: [
				{
					id: 'searchPrintHistory',
					label: 'Search Print History',
					isRestricted: restrictPrintHistory,
					visible: !(restrictPrintHistory && isHideUnavailableFeatures),
				},
				{
					id: 'requestPostageRefund',
					label: 'Request a Postage Refund',
				},
				{
					id: 'refundAssist',
					label: 'View Refund Assist Eligible Labels',
					showBadge: isShowRefundAssistLabelBadge, // Added mock data for demo
					badgeCount: refundAssistLabelCount,
					visible: Session.hasCapability(Capability.RefundAssistEnabled),
				},
				{
					id: 'fileInsuranceClaim',
					label: 'File an Insurance Claim',
				},
				{
					id: 'createScanForm',
					label: 'Create a SCAN Form',
					isRestricted: restrictScanForm,
					visible: !(restrictScanForm && isHideUnavailableFeatures),
				},
				{
					id: 'scheduleUspsPickUp',
					label: 'Schedule a USPS Pickup',
					isPlanUpgradeNeeded: !Session.hasCapability(Capability.CanUseSchedulePickup),
				},
				{
					id: 'createContainerLabel',
					label: 'Create a Container Label',
					visible: isGaPickupCarrier === true,
					showBadge: isShowContainerLabelBadge,
					badgeCount: containerLabelCount,
				},
			],
		};
	}

	static getReportsMenuItem() {
		const { isSignedIn } = Session.getData();
		let isHideUnavailableFeatures = false;
		let isReportsRestricted = false;

		if (isSignedIn) {
			isReportsRestricted = !(
				Session.hasCapability(InternalCapability.ViewAdvancedReporting) &&
				Session.hasCapability(InternalCapability.ViewReports)
			);
			isHideUnavailableFeatures = Session.hasCapability(
				InternalCapability.HideUnavailableFeatures
			);
		}

		return {
			id: 'reports',
			label: 'Reports',
			isRestricted: isReportsRestricted,
			isRootMenu: true,
			visible:
				!Session.hasCapability(InternalCapability.ZeroPostageBalance) &&
				!(isReportsRestricted && isHideUnavailableFeatures),
		};
	}

	static getSuppliesMenuItem() {
		const { isSignedIn, brand } = Session.getData();
		let hideChildren = false,
			isStoreRestricted = false;

		if (isSignedIn) {
			if (!Session.hasCapability(InternalCapability.PurchaseFromStore)) {
				isStoreRestricted = true;
				hideChildren = true;
			}
		}

		// hides child menu items for 'Supplies' when brand is not SDC
		if (brand.brandName !== 'Stamps.com') {
			hideChildren = true;
		}

		const supplies = {
			id: 'supplies',
			label: 'Supplies',
			isRestricted: isStoreRestricted,
			isRootMenu: true,
			items: [],
		};

		if (!hideChildren) {
			supplies.items = [
				{
					id: 'netstamps',
					label: 'NetStamps',
				},
				{
					id: 'labels',
					label: 'Labels',
				},
				{
					id: 'shippingSupplies',
					label: 'Shipping Supplies',
				},
				{
					id: 'envelopes',
					label: 'Mailing Supplies',
				},
				{
					id: 'freeUspsSupplies',
					label: 'Free USPS Supplies',
				},
			];
		}

		if (Session.canAccessPilotSuppliesStore()) {
			supplies.items = supplies.items.map(item => ({
				...item,
				navType: 'newTab'
			}));
		}

		return supplies;
	}

	static getShipstationUpgradeMenuItem() {
		const { calculatedSettings, preferences, settings } = Session.getData();
		// checks if shipstation upgrade link has to be shown
		const canSeeShipstationUpgrade =
			settings.CanSeeShipStationUpgrade && calculatedSettings.isSso;
		const hasUpgradedToShipstation = !preferences.HasUpgradedToShipStation;

		let shipStationUpgradeItem = {
			id: 'shipStationUpgrade',
			visible: canSeeShipstationUpgrade === true,
		};

		if (canSeeShipstationUpgrade) {
			if (hasUpgradedToShipstation) {
				shipStationUpgradeItem = {
					...shipStationUpgradeItem,
					text1: 'More Shipping Features',
					text2: 'Available in ShipStation',
					highlightText: true,
				};
			} else {
				shipStationUpgradeItem = {
					...shipStationUpgradeItem,
					text1: 'Get a free, 60-day upgrade',
					text2: 'to ShipStation. Give it a try!',
				};
			}
		}

		return shipStationUpgradeItem;
	}

	static getBalanceMenuItem() {
		const { balance, settings } = Session.getData();
		const { planDetails = {} } = Session.getAccountInfo();
		const { isPayAsYouGo } = planDetails;
		const availableBalance = isNumber(balance?.amountAvailable) ? balance.amountAvailable : 0;

		// check if balance should be shown based on Page and Capabilities
		const page = settings.Page;
		const showBalanceMenuItem =
			page.search(/mail|orders|products|contacts|history|reports|inbounds/i) !== -1 &&
			(Session.hasCapability(Capability.Shipping) ||
				Session.hasCapability(Capability.NetStamps) ||
				Session.hasCapability(Capability.International)) &&
			!Session.hasCapability(Capability.Invoicing);

		// create our number formatter to convert to USD currency
		const formatter = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
		});

		// check if unavailable features should be hidden
		const hideUnavailableFeatures = Session.hasCapability(
			InternalCapability.HideUnavailableFeatures
		);

		// check if a lock icon should appear for "Add Funds" menu item
		const restrictBuyMore = !Session.hasCapability(Capability.AddFunds);

		// check if a lock icon should appear for "View Purchase History" menu item
		const restrictPurchaseHistory = !(
			Session.hasCapability(InternalCapability.ViewReports) &&
			Session.hasCapability(InternalCapability.ViewAdvancedReporting)
		);

		// check if a lock icon should appear for "Change Payment Method" menu item
		const restrictPaymentMethod = !Session.hasCapability(
			InternalCapability.ChangePaymentMethod
		);

		// Hide 'Add Funds' for PAYG users with positive balance
		const hideBuyMore = isPayAsYouGo && availableBalance > 0;

		if (showBalanceMenuItem) {
			return {
				id: 'balance',
				label: 'Balance',
				balance: formatter.format(availableBalance),
				isRootMenu: true,
				items: [
					{
						id: 'addFunds',
						label: 'Add Funds',
						visible: !hideBuyMore && !(restrictBuyMore && hideUnavailableFeatures),
						isRestricted: restrictBuyMore,
					},
					{
						id: 'viewPurchaseHistory',
						label: 'View Purchase History',
						visible: !(restrictPurchaseHistory && hideUnavailableFeatures),
						isRestricted: restrictPurchaseHistory,
					},
					{
						id: 'changePaymentMethod',
						label: 'Change Payment Method',
						visible: !(restrictPaymentMethod && hideUnavailableFeatures),
						isRestricted: restrictPaymentMethod,
					},
				],
			};
		}

		return {
			visible: false,
		};
	}

	static getMyAccountMenuItem() {
		const { accountInfo, settings } = Session.getData();
		const { Page = '' } = settings;

		const showShipstationUpgrade = this.screenType === 'desktop',
			shipStationUpgradeItem = showShipstationUpgrade
				? this.getShipstationUpgradeMenuItem()
				: { visible: false };
		let username = 'My Account';
		if (this.screenType === 'desktop') {
			username = accountInfo.customerDetails?.username || '';
		}

		return {
			id: 'userName',
			label: username,
			isRootMenu: true,
			items: [
				{
					id: 'manageAccount',
					label: 'Manage Account',
					visible: Page !== 'account',
				},
				this.getLegalTerms(),
				this.getSupplies(),

				// should only render for desktop
				shipStationUpgradeItem,

				{
					id: 'signOut',
					label: 'Sign Out',
				},
			],
		};
	}

	static getLegalTerms() {
		return {
			id: 'legalTerms',
			label: 'Legal Terms',
			navType: 'newTab',
		};
	}

	static getSupplies() {
		return {
			id: 'supplies',
			label: 'Get Supplies',
			className: 'get-supplies',
			navType: Session.canAccessPilotSuppliesStore() ? 'newTab' : null,
		};
	}

	static getHelpMenuItem() {
		return {
			id: 'help',
			label: 'Help',
			isRootMenu: true,
			items: [
				{
					id: 'helpChatbot',
					label: 'Chat with us',
					className: 'help-chatbot',
					visible: this.showChatWithUs,
				},
				{
					id: 'helpCenter',
					label: 'Help Center',
					navType: 'newTab',
					className: 'help-center',
				},
			],
		};
	}

	static getSignInMenuItem() {
		return {
			id: 'signIn',
			label: 'Sign In',
		};
	}
}

export default HeaderDataBuilder;
